import { useState, useCallback } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  TextInput,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { isFlagEnabled } from '@/lib/featureFlags';
import { useAsyncResource } from '@/lib/useAsyncResource';
import { AsyncState } from '@/components/AsyncState';

const log = createLogger('NOTES');

interface ProfileNote {
  id: string;
  subject_profile_id: string;
  author_profile_id: string;
  visibility: 'private_to_author' | 'shared_with_subject' | 'gym_staff';
  note: string;
  created_at: string;
  updated_at: string;
}

interface Profile {
  id: string;
  username: string;
  display_name: string;
}

interface NotesData {
  notes: ProfileNote[];
  profiles: Profile[];
}

export default function NotesScreen() {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [noteText, setNoteText] = useState('');
  const [visibility, setVisibility] = useState<'private_to_author' | 'shared_with_subject' | 'gym_staff'>('private_to_author');
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadNotesData = useCallback(async (): Promise<NotesData> => {
    if (!profile) throw new Error('No profile');

    const { data: notesData, error: notesError } = await supabase
      .from('profile_notes')
      .select('*')
      .eq('author_profile_id', profile.id)
      .order('created_at', { ascending: false });

    if (notesError) throw notesError;

    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, display_name')
      .limit(100);

    if (profilesError) throw profilesError;

    return {
      notes: notesData || [],
      profiles: profilesData || [],
    };
  }, [profile]);

  const { data, loading, error, reload } = useAsyncResource<NotesData>(
    loadNotesData,
    [profile],
    {
      enabled: !!profile,
      onError: (err: any) => {
        log.error('Failed to load notes:', err);
      },
    }
  );

  const notes = data?.notes || [];
  const profiles = data?.profiles || [];

  const handleCreateNote = async () => {
    if (!profile) return;
    if (!selectedSubjectId) {
      setErrorMessage('Please select a subject');
      return;
    }
    if (!noteText.trim()) {
      setErrorMessage('Please enter a note');
      return;
    }

    setActionLoading(true);
    setErrorMessage('');
    try {
      const { error } = await supabase
        .from('profile_notes')
        .insert({
          subject_profile_id: selectedSubjectId,
          author_profile_id: profile.id,
          visibility,
          note: noteText.trim(),
        });

      if (error) throw error;

      Alert.alert('Saved', 'Note Saved');
      setNoteText('');
      setSelectedSubjectId('');
      setVisibility('private_to_author');
      await reload();
    } catch (err: any) {
      log.error('Failed to create note:', err);
      setErrorMessage(err.message || 'Failed to create note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    Alert.alert(
      'Delete Note',
      'Are you sure you want to delete this note?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const { error } = await supabase
                .from('profile_notes')
                .delete()
                .eq('id', noteId);

              if (error) throw error;
              await reload();
            } catch (err: any) {
              log.error('Failed to delete note:', err);
              Alert.alert('Oops', "Couldn't delete that note. Please try again.");
            }
          },
        },
      ]
    );
  };

  if (!isFlagEnabled('NOTES_ENABLED')) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }} edges={['top']}>
        <StatusBar style="light" />
        <View style={[styles.headerRow, { backgroundColor: theme.colors.background }]}>
          <View style={{ width: 48 }} />
          <View style={styles.headerTitleWrap}>
            <Text style={[styles.headerTitleText, { color: theme.colors.text }]}>Notes</Text>
          </View>
          <View style={{ width: 48 }} />
        </View>
        <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
          <Text style={[styles.disabledText, { color: theme.colors.textSecondary }]}>
            Notes feature is currently disabled
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const isCreateDisabled = !selectedSubjectId || !noteText.trim() || actionLoading;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }} edges={['top']}>
      <StatusBar style="light" />
      <View style={[styles.headerRow, { backgroundColor: theme.colors.background }]}>
        <View style={{ width: 48 }} />
        <View style={styles.headerTitleWrap}>
          <Text style={[styles.headerTitleText, { color: theme.colors.text }]}>Notes</Text>
        </View>
        <View style={{ width: 48 }} />
      </View>

      <AsyncState
        loading={loading}
        error={error}
        onRetry={reload}
        loadingTitle="Loading notes..."
      >

      <ScrollView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
        contentContainerStyle={{ paddingBottom: 28 }}
      >
        <View style={[styles.section, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Create Note</Text>

          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Subject</Text>
          <View style={[styles.picker, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border }]}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pickerContent}>
              {profiles.map((p) => (
                <TouchableOpacity
                  key={p.id}
                  style={[
                    styles.pickerOption,
                    selectedSubjectId === p.id && { backgroundColor: theme.colors.accent },
                  ]}
                  onPress={() => setSelectedSubjectId(p.id)}
                >
                  <Text
                    style={[
                      styles.pickerOptionText,
                      { color: selectedSubjectId === p.id ? '#fff' : theme.colors.text },
                    ]}
                  >
                    {p.username}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Note</Text>
          <TextInput
            style={[styles.textArea, { backgroundColor: theme.colors.surfaceVariant, color: theme.colors.text, borderColor: theme.colors.border }]}
            placeholder="Enter your note here..."
            placeholderTextColor={theme.colors.textSecondary}
            value={noteText}
            onChangeText={setNoteText}
            multiline
            numberOfLines={4}
          />

          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Visibility</Text>
          <View style={styles.visibilityRow}>
            <TouchableOpacity
              style={[
                styles.visibilityButton,
                { borderColor: theme.colors.border },
                visibility === 'private_to_author' && { backgroundColor: theme.colors.accent },
              ]}
              onPress={() => setVisibility('private_to_author')}
            >
              <Text
                style={[
                  styles.visibilityText,
                  { color: visibility === 'private_to_author' ? '#fff' : theme.colors.text },
                ]}
              >
                Private
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.visibilityButton,
                { borderColor: theme.colors.border },
                visibility === 'shared_with_subject' && { backgroundColor: theme.colors.accent },
              ]}
              onPress={() => setVisibility('shared_with_subject')}
            >
              <Text
                style={[
                  styles.visibilityText,
                  { color: visibility === 'shared_with_subject' ? '#fff' : theme.colors.text },
                ]}
              >
                Shared
              </Text>
            </TouchableOpacity>
            {isFlagEnabled('GYM_STAFF_NOTES_ENABLED') && (
              <TouchableOpacity
                style={[
                  styles.visibilityButton,
                  { borderColor: theme.colors.border },
                  visibility === 'gym_staff' && { backgroundColor: theme.colors.accent },
                ]}
                onPress={() => setVisibility('gym_staff')}
              >
                <Text
                  style={[
                    styles.visibilityText,
                    { color: visibility === 'gym_staff' ? '#fff' : theme.colors.text },
                  ]}
                >
                  Gym Staff
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {errorMessage ? (
            <Text style={[styles.errorText, { color: theme.colors.error }]}>{errorMessage}</Text>
          ) : null}

          <TouchableOpacity
            style={[
              styles.primaryButton,
              { backgroundColor: isCreateDisabled ? theme.colors.surfaceVariant : theme.colors.accent },
            ]}
            onPress={handleCreateNote}
            disabled={isCreateDisabled}
          >
            <Text style={[styles.buttonText, { color: isCreateDisabled ? theme.colors.textSecondary : '#fff' }]}>
              Create Note
            </Text>
          </TouchableOpacity>
        </View>

        <View style={[styles.section, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>My Notes</Text>
          {notes.length === 0 ? (
            <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>You have no notes yet</Text>
          ) : (
            notes.map((note) => {
              const subject = profiles.find((p) => p.id === note.subject_profile_id);
              return (
                <View key={note.id} style={[styles.noteCard, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border }]}>
                  <Text style={[styles.noteSubject, { color: theme.colors.text }]}>
                    Subject: {subject?.username || 'Unknown'}
                  </Text>
                  <Text style={[styles.noteText, { color: theme.colors.text }]}>{note.note}</Text>
                  <Text style={[styles.noteMeta, { color: theme.colors.textSecondary }]}>
                    {note.visibility} • {note.created_at ? new Date(note.created_at).toLocaleDateString() : '—'}
                  </Text>
                  <TouchableOpacity
                    style={[styles.deleteButton, { backgroundColor: theme.colors.error }]}
                    onPress={() => handleDeleteNote(note.id)}
                  >
                    <Text style={[styles.buttonText, { color: '#fff' }]}>Delete</Text>
                  </TouchableOpacity>
                </View>
              );
            })
          )}
        </View>
      </ScrollView>
      </AsyncState>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 8, minHeight: 44 },
  headerButton: { padding: 8, minWidth: 48, minHeight: 44, justifyContent: 'center' },
  headerButtonText: { fontSize: 15, fontWeight: '600' },
  headerTitleWrap: { flex: 1, alignItems: 'center' },
  headerTitleText: { fontSize: 15, fontWeight: '700' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  disabledText: { fontSize: 16, fontWeight: '600' },
  section: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 16 },
  sectionTitle: { fontSize: 18, fontWeight: '800', marginBottom: 12 },
  label: { fontSize: 14, fontWeight: '600', marginBottom: 8, marginTop: 8 },
  picker: { borderWidth: 1, borderRadius: 2, padding: 8, marginBottom: 12, maxHeight: 60 },
  pickerContent: { gap: 8 },
  pickerOption: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 4 },
  pickerOptionText: { fontSize: 14, fontWeight: '600' },
  textArea: { borderWidth: 1, borderRadius: 2, padding: 12, fontSize: 15, minHeight: 100, textAlignVertical: 'top', marginBottom: 12 },
  visibilityRow: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  visibilityButton: { flex: 1, paddingVertical: 10, borderRadius: 4, alignItems: 'center', borderWidth: 1 },
  visibilityText: { fontSize: 14, fontWeight: '600' },
  errorText: { fontSize: 14, marginBottom: 12 },
  primaryButton: { paddingVertical: 12, paddingHorizontal: 16, borderRadius: 2, alignItems: 'center' },
  buttonText: { fontSize: 15, fontWeight: '600' },
  emptyText: { fontSize: 14, fontStyle: 'italic' },
  noteCard: { borderWidth: 1, borderRadius: 2, padding: 12, marginBottom: 12 },
  noteSubject: { fontSize: 15, fontWeight: '700', marginBottom: 6 },
  noteText: { fontSize: 14, marginBottom: 8 },
  noteMeta: { fontSize: 12, marginBottom: 8 },
  deleteButton: { paddingVertical: 8, borderRadius: 4, alignItems: 'center' },
});
